
import React from 'react';
import { Transaction, CreditCard } from '../types';
import { formatCurrency, formatDate } from '../utils/helpers';
import { CATEGORY_CONFIG } from '../constants';

interface CardStatementModalProps {
  isOpen: boolean;
  onClose: () => void;
  card: CreditCard;
  transactions: Transaction[];
  monthName: string;
  totalAmount: number;
}

const CardStatementModal: React.FC<CardStatementModalProps> = ({ 
  isOpen, 
  onClose, 
  card, 
  transactions, 
  monthName,
  totalAmount
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-end md:items-center justify-center z-[110] p-0 md:p-4 animate-in fade-in duration-200">
      <div 
        className="bg-white dark:bg-slate-900 w-full max-w-lg rounded-t-[2.5rem] md:rounded-[2.5rem] shadow-2xl animate-in slide-in-from-bottom duration-300 border-t border-slate-100 dark:border-slate-800 flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 md:p-8 border-b border-slate-50 dark:border-slate-800 shrink-0">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-xl font-black text-slate-900 dark:text-slate-50 tracking-tight uppercase">Extrato da Fatura</h2>
              <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-1">
                {card.name} • {monthName}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="w-10 h-10 bg-slate-50 dark:bg-slate-800 rounded-full text-slate-400 flex items-center justify-center hover:text-slate-600 transition-colors"
            >
              <i className="fa-solid fa-times"></i>
            </button>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl flex justify-between items-center">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total da Fatura</span>
            <span className="text-lg font-black text-blue-600 dark:text-blue-400">{formatCurrency(totalAmount)}</span>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-4">
          {transactions.length === 0 ? (
            <div className="py-12 text-center text-slate-300 dark:text-slate-700">
              <i className="fa-solid fa-receipt text-4xl mb-4 opacity-20"></i>
              <p className="text-[10px] font-black uppercase tracking-widest">Nenhuma compra nesta fatura</p>
            </div>
          ) : (
            transactions.map((t) => {
              const categoryInfo = CATEGORY_CONFIG[t.category] || CATEGORY_CONFIG['Outros'];
              return (
                <div key={t.id} className="flex items-center gap-4 group">
                  <div className={`${categoryInfo.color} w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0 shadow-sm text-sm`}>
                    {categoryInfo.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-slate-800 dark:text-slate-100 text-xs truncate leading-tight">
                      {t.description}
                    </h4>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
                      {formatDate(t.date)} • {t.category}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-mono font-black text-xs ${t.type === 'income' ? 'text-emerald-600' : 'text-red-500'}`}>
                      {t.type === 'income' ? '+' : '-'} {formatCurrency(t.amount)}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-6 md:p-8 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800 shrink-0">
          <button 
            onClick={onClose}
            className="w-full py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-[10px] font-black uppercase tracking-widest text-slate-600 dark:text-slate-300 hover:bg-slate-50 transition-all"
          >
            Fechar Extrato
          </button>
        </div>
      </div>
    </div>
  );
};

export default CardStatementModal;
